import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuthStore } from "./state/authStore";
import { useTeamStore } from "./state/teamStore";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import Login from "./pages/Login";
import RunDetail from "./pages/RunDetail";
import MasterComparison from "./pages/MasterComparison";
import HeadToHead from "./pages/HeadToHead";
import TeamSettings from "./pages/TeamSettings";
import JoinTeam from "./pages/JoinTeam";
import FuelPlanner from "./pages/FuelPlanner";
import HandlingPanel from "./pages/HandlingPanel";
import StintsPanel from "./pages/StintsPanel";
import PitAdjustments from "./pages/PitAdjustments";
import Checklist from "./pages/Checklist";
import ChecklistTemplates from "./pages/ChecklistTemplates";
import Settings from "./pages/Settings";
import WeekWorkspace from "./pages/WeekWorkspace";
import WeekOverview from "./pages/WeekOverview";
import TeamsHome from "./pages/TeamsHome";
import TeamHome from "./pages/TeamHome";
import SeriesHome from "./pages/SeriesHome";
import TeamOnboarding from "./pages/TeamOnboarding";
import DriversBoard from "./pages/DriversBoard";
import QualifyingBoard from "./pages/QualifyingBoard";
import DesktopAgent from "./pages/DesktopAgent";
import CrewChief from "./pages/CrewChief";
import StreamOverlay from "./pages/StreamOverlay";
import SetupLibrary from "./pages/SetupLibrary";
import SetupDiff from "./pages/SetupDiff";
import Trends from "./pages/Trends";
import TrackTempModelHealth from "./pages/TrackTempModelHealth";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Signup from "./pages/Signup";
import AdminConsole from "./pages/AdminConsole";

const Landing = lazy(() => import("./pages/Landing"));

export default function App() {
  const { checkAuth, user } = useAuthStore();
  const { fetchTeams } = useTeamStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) fetchTeams();
  }, [user, fetchTeams]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          user ? (
            <Navigate to="/teams" replace />
          ) : (
            <Suspense fallback={null}>
              <Landing />
            </Suspense>
          )
        }
      />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/stream-overlay" element={<StreamOverlay />} />
      <Route path="/join/:token" element={<ProtectedRoute><JoinTeam /></ProtectedRoute>} />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<AdminConsole />} />
        <Route path="/onboarding/team/new" element={<TeamOnboarding />} />
        <Route path="/teams" element={<TeamsHome />} />
        <Route path="/teams/:teamId" element={<TeamHome />} />
        <Route path="/teams/:teamId/settings" element={<TeamSettings />} />
        <Route path="/teams/:teamId/checklists" element={<ChecklistTemplates />} />
        <Route path="/teams/:teamId/model-health" element={<TrackTempModelHealth />} />
        <Route path="/teams/:teamId/setups" element={<SetupLibrary />} />
        <Route path="/teams/:teamId/setups/compare" element={<SetupDiff />} />
        <Route path="/teams/:teamId/series/:seriesSlug" element={<SeriesHome />} />
        <Route path="/teams/:teamId/series/:seriesSlug/weeks/:trackWeekId" element={<WeekWorkspace />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<WeekOverview />} />
          <Route path="drivers" element={<DriversBoard />} />
          <Route path="qualifying" element={<QualifyingBoard />} />
          <Route path="master" element={<MasterComparison />} />
          <Route path="head-to-head" element={<HeadToHead />} />
          <Route path="fuel" element={<FuelPlanner />} />
          <Route path="stints" element={<StintsPanel />} />
          <Route path="handling" element={<HandlingPanel />} />
          <Route path="pit" element={<PitAdjustments />} />
          <Route path="checklist" element={<Checklist />} />
          <Route path="setup-diff" element={<Navigate to="../head-to-head" replace />} />
          <Route path="trends" element={<Trends />} />
        </Route>
        <Route path="/runs/:id" element={<RunDetail />} />
        <Route path="/teams/:teamId/live" element={<CrewChief />} />
        <Route path="/desktop" element={<DesktopAgent />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}
